import React from 'react';
import { ClinicInfoPanel } from '../clinicsettings/info/clinicInfoParts';

export const buildFeatureCategories = (clinicFeatures = {}) => [
    {
        title: 'Communication',
        icon: 'bi-chat-dots',
        features: [
            { name: 'Two-Way Texting', description: 'Enable bidirectional text communication', enabled: clinicFeatures.twoWayText, key: 'twoWayText', icon: 'bi-chat-left-text' },
            { name: 'Anti-Spam Compliance', description: 'Comply with anti-spam regulations', enabled: clinicFeatures.isAntispamLaw, key: 'isAntispamLaw', icon: 'bi-shield-check' },
            { name: 'Invalid Details Summary', description: 'Receive digest emails for invalid patient contact details', enabled: clinicFeatures.isInvalidSummary, key: 'isInvalidSummary', icon: 'bi-exclamation-octagon' },
            { name: 'Articles', description: 'Create and send patient education articles', enabled: clinicFeatures.isArticle, key: 'isArticle', icon: 'bi-file-text' },
        ],
    },
    {
        title: 'Reminders',
        icon: 'bi-bell',
        features: [
            { name: 'Appointment Reminders', description: 'Enable appointment reminder workflows for the clinic', enabled: clinicFeatures.reminder, key: 'reminder', icon: 'bi-bell' },
            { name: 'Email Reminders', description: 'Send reminders via email', enabled: clinicFeatures.reminderEmail, key: 'reminderEmail', icon: 'bi-envelope' },
            { name: 'SMS Reminders', description: 'Send reminders via text message', enabled: clinicFeatures.reminderSms, key: 'reminderSms', icon: 'bi-phone' },
            { name: 'Call Reminders', description: 'Send reminders via phone call', enabled: clinicFeatures.reminderCall, key: 'reminderCall', icon: 'bi-telephone' },
        ],
    },
    {
        title: 'Newsletter',
        icon: 'bi-envelope-paper',
        features: [
            { name: 'Email Newsletter', description: 'Send newsletters via email', enabled: clinicFeatures.newsletterEmail, key: 'newsletterEmail', icon: 'bi-envelope-open' },
            { name: 'SMS Newsletter', description: 'Send newsletters via text message', enabled: clinicFeatures.newsletterSms, key: 'newsletterSms', icon: 'bi-chat-text' },
        ],
    },
    {
        title: 'Marketing',
        icon: 'bi-megaphone',
        features: [
            { name: 'Email Blast', description: 'Send marketing emails', enabled: clinicFeatures.blastEmail, key: 'blastEmail', icon: 'bi-send' },
            { name: 'SMS Blast', description: 'Send marketing text messages', enabled: clinicFeatures.blastSms, key: 'blastSms', icon: 'bi-chat-dots' },
        ],
    },
    {
        title: 'Patient Engagement',
        icon: 'bi-people',
        features: [
            { name: 'Online Booking', description: 'Allow patients to book appointments online', enabled: clinicFeatures.booking, key: 'booking', icon: 'bi-calendar-check' },
            { name: 'Appointments', description: 'View and manage the clinic appointment schedule', enabled: clinicFeatures.isAppointments, key: 'isAppointments', icon: 'bi-calendar-event' },
            { name: 'Optical Dispensary', description: 'Manage pickups, contact lens reorder requests, and CL brands', enabled: clinicFeatures.isOpticalDispensary, key: 'isOpticalDispensary', icon: 'bi-eyeglasses' },
            { name: 'Patient Recall', description: 'Automated patient recall system', enabled: clinicFeatures.recall, key: 'recall', icon: 'bi-arrow-repeat' },
            { name: 'Survey Reminders', description: 'Send reminders to complete surveys', enabled: clinicFeatures.isSurvey, key: 'isSurvey', icon: 'bi-clipboard-check' },
        ],
    },
    {
        title: 'Reputation & Feedback',
        icon: 'bi-star',
        features: [
            { name: 'Reputation Management', description: 'Monitor and manage online reputation', enabled: clinicFeatures.reputation, key: 'reputation', icon: 'bi-star-half' },
            { name: 'Patient Surveys', description: 'Collect and analyze patient feedback', enabled: clinicFeatures.survey, key: 'survey', icon: 'bi-clipboard-data' },
        ],
    },
];

export const FeatureListItem = ({ feature }) => (
    <div
        className={`clinic-settings-record-item features-list-item ${
            feature.enabled ? 'features-list-item--enabled' : 'features-list-item--disabled'
        }`}
    >
        <span className="clinic-settings-record-item__avatar">
            <i className={`bi ${feature.icon}`} />
        </span>
        <div className="clinic-settings-record-item__main">
            <h6 className="clinic-settings-record-item__title">{feature.name}</h6>
            <p className="clinic-settings-record-item__meta mb-0">{feature.description}</p>
        </div>
        <div className="clinic-settings-record-item__actions features-list-item__actions">
            <span className={`clinic-settings-status-pill ${feature.enabled ? 'is-open' : 'is-closed'}`}>
                <i className={`bi ${feature.enabled ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} />
                {feature.enabled ? 'Enabled' : 'Disabled'}
            </span>
        </div>
    </div>
);

export const FeatureCategoryPanel = ({ category }) => {
    const enabledCount = category.features.filter((feature) => feature.enabled).length;

    return (
        <ClinicInfoPanel
            title={category.title}
            icon={category.icon}
            actions={
                <span className="ecx-page-chip">
                    <i className="bi bi-check-circle" />
                    {enabledCount}/{category.features.length} enabled
                </span>
            }
        >
            <div className="clinic-settings-record-list">
                {category.features.map((feature) => (
                    <FeatureListItem key={feature.key} feature={feature} />
                ))}
            </div>
        </ClinicInfoPanel>
    );
};

export const FeatureOverviewStats = ({ totalCount, enabledCount, disabledCount }) => (
    <div className="features-overview-stats">
        <div className="features-overview-stat">
            <span className="features-overview-stat__icon">
                <i className="bi bi-grid-3x3-gap" />
            </span>
            <div>
                <span className="features-overview-stat__label">Total Features</span>
                <span className="features-overview-stat__value">{totalCount}</span>
            </div>
        </div>
        <div className="features-overview-stat">
            <span className="features-overview-stat__icon is-enabled">
                <i className="bi bi-check-circle-fill" />
            </span>
            <div>
                <span className="features-overview-stat__label">Enabled</span>
                <span className="features-overview-stat__value">{enabledCount}</span>
            </div>
        </div>
        <div className="features-overview-stat">
            <span className="features-overview-stat__icon is-disabled">
                <i className="bi bi-x-circle-fill" />
            </span>
            <div>
                <span className="features-overview-stat__label">Disabled</span>
                <span className="features-overview-stat__value">{disabledCount}</span>
            </div>
        </div>
    </div>
);

export const FeatureCategoryPanels = ({ categories }) => (
    <div className="features-category-panels">
        {categories.map((category) => (
            <FeatureCategoryPanel key={category.title} category={category} />
        ))}
    </div>
);

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@eyeconx.com';

export const FeatureSupportNote = () => (
    <div className="features-support-note" role="note">
        <span className="features-support-note__icon" aria-hidden="true">
            <i className="bi bi-info-circle" />
        </span>
        <div className="features-support-note__copy">
            <strong>Need to enable or disable a feature?</strong>
            <p className="mb-0">
                EyeconX features are managed by our team. To change, enable, or disable any feature
                for your clinic, please reach out to EyeconX support at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}?subject=EyeconX%20Feature%20Request`}>
                    {SUPPORT_EMAIL}
                </a>
                .
            </p>
        </div>
    </div>
);
